/**
 * Decorative SVG illustration of a project-management workflow:
 * three kanban-style cards connected by dotted lines, with subtle accent dots.
 * Uses Tailwind color tokens via CSS vars so it adapts if we re-theme.
 */
export function WorkflowHero({
  className,
  ariaLabel,
}: {
  className?: string;
  ariaLabel?: string;
}): JSX.Element {
  return (
    <svg
      viewBox="0 0 480 320"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label={ariaLabel ?? 'Workflow illustration'}
    >
      <defs>
        <linearGradient id="bg-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#eff6f8" />
          <stop offset="100%" stopColor="#fafaf7" />
        </linearGradient>
        <linearGradient id="card-grad-1" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#f4f3ed" />
        </linearGradient>
        <filter id="card-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#1c3a48" floodOpacity="0.10" />
        </filter>
      </defs>

      {/* Soft background */}
      <rect width="480" height="320" fill="url(#bg-grad)" rx="16" />

      {/* Decorative grid dots */}
      <g fill="#1d5770" opacity="0.06">
        {Array.from({ length: 8 }).map((_, row) =>
          Array.from({ length: 12 }).map((__, col) => (
            <circle key={`${row}-${col}`} cx={20 + col * 40} cy={20 + row * 38} r="1.5" />
          )),
        )}
      </g>

      {/* Connector dotted lines between cards */}
      <path
        d="M 145 130 Q 175 130 175 170 Q 175 210 215 210"
        stroke="#458ea3"
        strokeWidth="1.5"
        fill="none"
        strokeDasharray="3 5"
      />
      <path
        d="M 305 210 Q 345 210 345 170 Q 345 130 375 130"
        stroke="#458ea3"
        strokeWidth="1.5"
        fill="none"
        strokeDasharray="3 5"
      />

      {/* Card 1 — top-left (initial stage, colored bar = brand) */}
      <g filter="url(#card-shadow)">
        <rect x="50" y="60" width="120" height="80" rx="10" fill="url(#card-grad-1)" stroke="#e9e7dc" />
        <rect x="50" y="60" width="4" height="80" rx="2" fill="#266f87" />
        <rect x="66" y="76" width="60" height="6" rx="3" fill="#1c3a48" />
        <rect x="66" y="92" width="40" height="4" rx="2" fill="#7d7a6b" />
        <circle cx="68" cy="118" r="3" fill="#266f87" />
        <rect x="78" y="115" width="40" height="6" rx="3" fill="#aba896" />
      </g>

      {/* Card 2 — middle (in-progress stage, colored bar = accent) */}
      <g filter="url(#card-shadow)">
        <rect x="195" y="170" width="120" height="80" rx="10" fill="url(#card-grad-1)" stroke="#e9e7dc" />
        <rect x="195" y="170" width="4" height="80" rx="2" fill="#e7ad29" />
        <rect x="211" y="186" width="70" height="6" rx="3" fill="#1c3a48" />
        <rect x="211" y="202" width="50" height="4" rx="2" fill="#7d7a6b" />
        <rect x="211" y="222" width="22" height="14" rx="7" fill="#fdf8eb" stroke="#f5db8d" />
        <circle cx="218" cy="229" r="2" fill="#a36818" />
      </g>

      {/* Card 3 — top-right (terminal stage, colored bar = green) */}
      <g filter="url(#card-shadow)">
        <rect x="340" y="60" width="120" height="80" rx="10" fill="url(#card-grad-1)" stroke="#e9e7dc" />
        <rect x="340" y="60" width="4" height="80" rx="2" fill="#10b981" />
        <rect x="356" y="76" width="60" height="6" rx="3" fill="#1c3a48" />
        <rect x="356" y="92" width="40" height="4" rx="2" fill="#7d7a6b" />
        <path d="M 358 117 l 4 4 l 8 -8" stroke="#10b981" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="376" y="113" width="36" height="6" rx="3" fill="#a7f3d0" />
      </g>

      {/* Floating accent dots */}
      <circle cx="30" cy="200" r="3" fill="#e7ad29" opacity="0.7" />
      <circle cx="450" cy="240" r="3" fill="#e7ad29" opacity="0.7" />
      <circle cx="240" cy="40" r="2.5" fill="#266f87" opacity="0.6" />
      <circle cx="120" cy="280" r="2.5" fill="#266f87" opacity="0.6" />
    </svg>
  );
}
